import { IAssignEntity } from "../basics/basics.models";
import { BaseValidationResponse } from "../validations/validations.models";
import { IInspectionStateEntity } from "../rightship-inspections/rightship-inspections.models";
import { DataGridColumn, DataType } from "../queries/queries.models";
import { MobileHelper } from "../../helpers/mobile-helper";
import { HistoryHelper } from "../../helpers/history-helper";
import { TasksConstants } from "../tasks/task.model";
import * as moment from "moment";
import { FromNowValueConverter } from "../../views/components/converters/date/date-format";
import { IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";
import { Claims } from "../../constants/authentication/claims";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { Container } from "aurelia-dependency-injection";

export interface IncidentModel extends IAssignEntity {
    incidentId?: number;
    vesselId?: number;
    incidentNumber?: number;
    incidentDate?: Date;
    incidentLocationId?: number;
    incidentDetailedStatusId?: number;
    incidentSourceId?: number;
    startLatitude?: number;
    startLongitude?: number;
    incidentZoneId?: number;
    flagId?: number;
    registeredOwnerId?: number;
    propulsionTypeAtTimeOfIncidentId?: number;
    casualtyOrDemolitionId?: number;
    removalFromSceneId?: number;
    vesselWasUnderPilotage?: boolean;
    isTotalLoss?: boolean;
    casualtyTypeId?: number;
    wasAssistanceGiven?: boolean;
    pollutionOccured?: boolean;
    pollutionDetails?: string;
    numberKilled?: number;
    numberMissing?: number;
    incidentSeverityId?: number;
    precisText?: string;
    complimentaryText?: string;
    otherVesselInvolvedId1?: number;
    otherVesselInvolvedId2?: number;
    otherVesselInvolvedId3?: number;
    moreThanThreeOtherVesselsInvolved: boolean;
    startPortId?: number;
    voyageFrom?: string;
    voyageTo?: string;
    cargoDescription?: string;
    isDangerousCargo?: boolean;
    cargoStatusId?: number;
    incidentDetailCategoryId?: number;
    incidentDetailSubCategoryId?: number;
    likelyToOccurInVessel?: boolean;
    likelyToOccurInFleet?: boolean;
    information?: string;
    preventativeActionText?: string;
    casualtyTypeOverrideId?: number;
    numberKilledOverride?: number;
    numberMissingOverride?: number;
    numberSeriousInjuries?: number;
    incidentSeverityOverrideId?: number;
    closed: boolean;
    closeOutComments?: string;
    assignedTo?: number;
    assignedDate?: Date;
    assignmentComment?: string;
    incidentReasonForCloseOutId?: number;
    incidentEvents: IncidentEventModel[];
    reportedOnBehalfOf?: string;
    rootCauseAnalysisResponseRatingId?: number;
    correctiveActionResponseRatingId?: number;
    preventiveActionResponseRatingId?: number;
    sustainedSignificantDamage?: boolean;
    vesselWasUnderPilotageOverride?: boolean;
    sustainedSignificantDamageOverride?: boolean;
    pollutionOccuredOverride?: boolean;
    isTotalLossOverride?: boolean;
    wasVesselRenderedUnseaworthy?: boolean;
    wasVesselRenderedUnseaworthyOverride?: boolean;
}

export interface IncidentOverrideModel {
    incidentOverrideId: number;
    incidentId: number;
    incidentReasonForOverrideId: number;
    overridenComputedSeverityId: number;
    justification: string;
    overridenGracePeriod?: number;
    overridenUseInSafetyScore: boolean;
}

export interface IncidentEventModel {
    incidentEventId?: number;
    eventDate?: Date;
    incidentEventInitialCauseId?: number;
    incidentEventActionId?: number;
    incidentEventComponentId?: number;
    incidentEventPositionId?: number;
    incidentEventExternalFactorId?: number;
    incidentEventWeatherId?: number;
    isActive: boolean;
}

export interface IncidentClientModel extends IEntityTypeIdentifier, IInspectionStateEntity {
    incidentStatusId: number;
    incidentStatus: string;
    id: number;
    vesselDateOfBuild: Date;
    vesselDwt: number;
    closed: boolean;
    incidentId: number;
    assignedUserId: number;
    assignedTo?: string;
    vesselType: string;
    statuses: IncidentStatusClientModel[];
    vesselImo: string;
    vehicleIdentificationNumber?: string;
    europeanNumberOfIdentification?: string;
    shipManagerEmail?: string;
    vesselId: number;
    closeoutSubtaskId?: number;
    closeoutSubtaskStatusId?: number;
    vesselName: string;
    zone: string;
    incidentNumber: string;
    latitude: string;
    longitude: string;
    incidentDate: Date;
    incidentDateString?: string;
    locationType: string;
    flag: string;
    detailedStatus: string;
    owner: string;
    source: string;
    propulsionType: string;
    casualtyOrDemolition: string;
    pollutionOccured: boolean;
    removalFromScene: string;
    pollutionDetails: string;
    wasVesselUnderPilotage: boolean;
    numberOfFatalities: number;
    totalLossIndicator: boolean;
    numberOfPeopleMissing: number;
    casualtyType: string;
    numberOfSeriousInjuries: number;
    assistanceGiven: boolean;
    severity: string;
    precisText: string;
    complimentaryText: string;
    rootCauseCategory: string;
    rootCauseSubCategory: string;
    occurInVessel: boolean;
    occurInFleet: boolean;
    preventativeAction: string;
    casualtyTypeOverride: string;
    numberOfFatalitiesOverride: number;
    numberOfPeopleMissingOverride: number;
    severityOverride: string;
    reasonForCloseOut: string;
    closeOutComments: string;
    otherVesselImo1: string;
    otherVesselName1: string;
    otherVesselImo2: string;
    otherVesselName2: string;
    otherVesselImo3: string;
    otherVesselName3: string;
    moreThanThreeVesselsInvolved: boolean;
    events: IncidentEventClientModel[];
    startPort: string;
    voyageFrom: string;
    voyageTo: string;
    cargo: string;
    dangerousCargo: boolean;
    cargoStatus: string;
    starRating: number;
    rating: string;
    misreportedReason?: string;
    reportedOnBehalfOf?: string;
    documentsCount?: number;
    privateNotesCount?: number;
    correspondenceCount?: number;
    computedSeverityId?: number;
    isInconsistentState: boolean;
    casualtyTypeId?: number;
    transparencyStatusId?: number;
    transparencyStatus?: string;
    remainingGracePeriod?: number;
    previousRemainingGracePeriod?: number;
}

export interface IncidentEventClientModel {
    eventDate: Date;
    eventInitial: string;
    eventAction: string;
    eventExternal: string;
    eventComponent: string;
    eventPosition: string;
    eventWeather: string;
}

export interface IncidentStatusClientModel {
    previousStatusId?: number;
    name: string;
    statusId: number;
    subtaskTypes?: number[];
    comment?: string;
    user: string;
    date: Date;
}

export interface SaveIncidentResponse extends BaseValidationResponse {
    generatedId?: number;
    computedSeverityId?: number;
    computedUseInSafetyScore?: boolean;
}

export interface OverrideIncidentDetailsResponse extends BaseValidationResponse {
    generatedId: number;
}

export interface ComputeIncidentGracePeriodRequest {
    incidentId: number;
    remainingDays: number;
}

export interface ComputeIncidentGracePeriodResponse {
    gracePeriod: number;
}

export enum IncidentReasonForOverrideEnum {
    IncidentSeverityShouldChange = 1,
    IncidentShouldAffectVesselSafetyScore = 2,
    IncidentShouldNotAffectVesselSafetyScore = 3,
    IncidentWasNonFault = 4,
    VesselNotTrading = 5,
    ChangeSafetyScoreGracePeriod = 6,
    ShouldBeMarkedAsTransparent = 7,
    ShouldNotBeMarkedAsTransparent = 8
}

export enum ComputedIncidentSeverityEnum {
    CategoryC = 1,
    CategoryB = 2,
    CategoryA = 3,
    CategoryD = 4,
    CategoryU = 5
}

export enum IncidentTabEnum {
    IncidentOverview = 1,
    IncidentDetails = 2,
    IncidentDocuments = 3,
    IncidentPrivateNotes = 4,
    IncidentCorrespondance = 5,
    IncidentHistory = 6
}

export enum TransparencyStatusEnum {
    Transparent = 1,
    NonTransparent = 2
}

export enum IncidentFilters {

    // Default Filters
    PendingSubtask = 1,
    PendingVet = 2,
    VettedLast12Mths = 3,

    // Status Filters
    Created = 4,
    InProgress = 8,
    AwaitingResponse = 16,
    IncidentReassignment = 32,
    Closed = 64,
    PendingReview = 128,
    Misreported = 256,
    NotInSafetyScore = 1024,

    Statuses = Created | InProgress | AwaitingResponse | IncidentReassignment | Closed | PendingReview | Misreported,
    PendingDueDiligence = 100
}

export class IncidentActivitiesColumns {
    public static getColumns(openInANewTab: boolean, rightShipUser: boolean, isFromVet: boolean, isCustomer: boolean) {
        const newTab = openInANewTab ? 'target="_blank"' : '';
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                columnOrder: 1,
                sortOrder: 2,
                dataType: DataType.CustomTemplate,
                name: 'vesselName',
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Name",
                value: '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<a class="link" href="/incident/${item.incidentId}" ' + newTab + ' mousedown.trigger="itemClick()">${item.vesselName}</a>' +
                    '<svg class="rs-badge" viewBox="0 0 30 30" if.bind="item.isRightShipMember && isRightShipUser">' +
                    '<g>' +
                    '<path fill="#003745" d="M2,0h26c1.1,0,2,0.9,2,2v26c0,1.1-0.9,2-2,2H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '<path fill="#FF5F1B" d="M2,0h1.5c0.5,0,1,0.2,1.4,0.6l15.5,14.8l0,0L4.9,29.5C4.5,29.8,4,30,3.5,30H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '</g>' +
                    '</svg>' +
                    '</div>' +
                    '<span if.bind="item.vesselImo">IMO ${item.vesselImo}</span>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span></div>',
                instancePerRow: true,
                isSortable: true,
                parameter: {
                    item: null,
                    itemClick: () => {
                        HistoryHelper.addState(TasksConstants.historyTaskTabKey, TasksConstants.INCListName);

                        return true;
                    },
                    isRightShipUser: rightShipUser
                }
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 6,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'submittedDate',
                title: isFromVet ? 'Date of Inspection' : 'Incident Date',
                valueConverter: (value) => moment(value).format("DD MMM YYYY")
            },
            <DataGridColumn>{
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'information',
                title: 'Information',
                value:
                    '<div class="information">' +
                    '<span>${getInformationString(item)}</span>' +
                    '</div>',
                parameter: {
                    item: null,
                    getInformationString(item: any) {
                        const userClaimsService = Container.instance.get(UserClaimsService) as UserClaimsService;
                        const canViewCategoryWhenGracePeriodActive = userClaimsService.hasPermission(Claims.CanViewCategoryWhenGracePeriodActive);
                        const parts = [
                            item.remainingGracePeriod > 0 && !canViewCategoryWhenGracePeriodActive ? null : item.incidentSeverity,
                            item.incidentSource,
                            item.casualtyType
                        ];
                        return parts.filter((part) => part).join(', ');
                    }
                }
            },
            <DataGridColumn>{
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'reviewer',
                title: 'Reviewer',
                value:
                    '<div class="reviewer" if.bind="item.reviewer">' +
                    '<span class="name">${item.reviewer}</span>' +
                    '<span class="type" if.bind="item.reviewerType">${item.reviewerType}</span>' +
                    '</div>',
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 6,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                name: 'status',
                title: 'Status',
                value: () => '<div class="status-badge-value" if.bind="item.status">' +
                    '<div class="row nowrap align-items-middle">' +
                    '<div class="status-badge-dot"></div>' +
                    '<span>${isCustomer && item.status == "Review Required"?"Pending Review":item.status}</span>' +
                    '</div>' +
                    '<span class="status-date">${getDateToString(item.statusDate)}</span>' +
                    '</div>',
                getCellClass: (item: { status: string }) => {
                    return (item && item.status) ? `status-badge ${item.status.toLowerCase().replace(/[^A-z0-9]+/g, '-')}` : '';
                },
                parameter: {
                    item: null,
                    isCustomer: isCustomer,
                    getDateToString: (date: Date | string) => this.getDateToString(date)
                }
            });
        return columns;
    }

    public static getDateToString(date: Date | string): string {
        if (!date)
            return 'N/A'

        let val: Date;

        if (typeof date == 'string')
            val = new Date(date);
        else
            val = date

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return 'N/A';

        return new FromNowValueConverter().toView(val);
    }
}